import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Paperclip, FileText } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import AccountPicker from '../../components/admin/AccountPicker';
import BulkActionToolbar from '../../components/admin/BulkActionToolbar';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import StatusBadge from '../../components/admin/StatusBadge';
import TicketDetailModal from '../../components/admin/TicketDetailModal';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';

const STATUS_META = {
  open: { label: 'Open', tone: 'accent' },
  in_progress: { label: 'In progress', tone: 'warning' },
  awaiting_reply: { label: 'Awaiting reply', tone: 'waiting' },
  resolved: { label: 'Resolved', tone: 'success' },
  closed: { label: 'Closed', tone: 'muted' },
};

const PRIORITY_META = {
  low: { label: 'Low', tone: 'neutral' },
  medium: { label: 'Medium', tone: 'warning' },
  high: { label: 'High', tone: 'warning' },
  critical: { label: 'Critical', tone: 'danger' },
};

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.endsWith('Z') ? '' : 'Z'));
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const ticketsData = await api.get('/api/admin/tickets');
      setTickets(ticketsData.tickets || []);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ?ticket=<id> (from a ticket thread in Communications) opens that ticket.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedId = searchParams.get('ticket');
  useEffect(() => {
    if (!linkedId || loading) return;
    const hit = tickets.find((x) => x.id === linkedId);
    if (hit) setSelected(hit);
  }, [linkedId, loading, tickets]);
  const closeDetail = () => {
    setSelected(null);
    if (linkedId) { const next = new URLSearchParams(searchParams); next.delete('ticket'); setSearchParams(next, { replace: true }); }
  };

  const onSaved = (updated) => {
    setTickets((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
  };

  const onDeleted = (id) => {
    setTickets((prev) => prev.filter((t) => t.id !== id));
    setSelected(null);
  };

  const toggleRow = (id) =>
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = (visibleIds) =>
    setCheckedIds((prev) => (visibleIds.every((id) => prev.includes(id))
      ? prev.filter((id) => !visibleIds.includes(id))
      : [...new Set([...prev, ...visibleIds])]));

  // Called by ConfirmDialog; it awaits this and shows any thrown error inline.
  const bulkResolve = async (ids) => {
      await api.post('/api/admin/tickets/bulk-resolve', { ids });
      setTickets((prev) => prev.map((t) => (ids.includes(t.id) ? { ...t, status: 'resolved' } : t)));
      setCheckedIds([]);
  };

  const resolvableIds = checkedIds.filter((id) => tickets.find((r) => r.id === id)?.status !== 'resolved');
  const alreadyResolvedCount = checkedIds.length - resolvableIds.length;

  const filters = useMemo(
    () => [
      {
        key: 'accountId',
        label: 'accounts',
        // The prod roster is ~1,900 accounts; a <select> fed by one page of
        // /api/admin/accounts only ever showed the first 50. AccountPicker
        // searches the full roster server-side.
        control: (value, setValue) => (
          <div className="min-w-[220px]">
            <AccountPicker value={value} onChange={setValue} emptyLabel="All accounts" />
          </div>
        ),
      },
      {
        key: 'status',
        label: 'statuses',
        options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
      },
      {
        key: 'priority',
        label: 'priorities',
        options: Object.entries(PRIORITY_META).map(([value, meta]) => ({ value, label: meta.label })),
      },
    ],
    []
  );

  const columns = [
    {
      key: 'title',
      header: 'Ticket',
      sortable: true,
      render: (row) => (
        <div className="flex items-start gap-2.5">
          <FileText className="w-3.5 h-3.5 text-gray-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-white font-medium truncate" title={row.title}>{row.title}</div>
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">
              {row.submittedBy?.name || 'Unknown'} · {row.accountName}
              {row.attachmentCount > 0 && (
                <span className="ml-1.5 inline-flex items-center gap-0.5 text-gray-600">
                  · <Paperclip className="w-2.5 h-2.5" /> {row.attachmentCount}
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      sortable: true,
      render: (row) => {
        const meta = PRIORITY_META[row.priority] || PRIORITY_META.medium;
        return <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => {
        const meta = STATUS_META[row.status] || STATUS_META.open;
        return (
          <div className="flex flex-col items-start gap-1">
            <StatusBadge tone={meta.tone}>{meta.label}</StatusBadge>
            {row.newReply && <StatusBadge tone="accent" className="!py-0.5">New reply</StatusBadge>}
          </div>
        );
      },
    },
    {
      key: 'createdAt',
      header: 'Opened',
      sortable: true,
      align: 'right',
      render: (row) => <span className="text-[12px] text-gray-400">{formatDate(row.createdAt)}</span>,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Tickets</span></h1>
          <p className="text-sm text-gray-500 mt-1">
            Support tickets across all accounts. Click a row to update status or add internal notes.
          </p>
        </div>
      </div>

      {loadError && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/[0.08] border border-red-500/20 text-[13px] text-red-300/90">
          {loadError}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={tickets}
        searchableKeys={['title', 'description', 'accountName']}
        filters={filters}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        loading={loading}
        emptyMessage="No tickets have been submitted yet."
        onRowClick={(row) => setSelected(row)}
        selectable
        selectedIds={checkedIds}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
      />

      <BulkActionToolbar
        selected={checkedIds}
        onClear={() => setCheckedIds([])}
        actions={[{
          key: 'resolve',
          // Only items not already resolved count toward the action; the
          // server skips resolved rows anyway, but the label should tell the
          // truth about what one click will do.
          label: `Mark ${resolvableIds.length} resolved${alreadyResolvedCount ? ` (${alreadyResolvedCount} already resolved)` : ''}`,
          disabled: resolvableIds.length === 0,
          hint: resolvableIds.length === 0 ? 'Everything selected is already resolved' : undefined,
          onRun: () => setConfirmBulk(true),
        }]}
      />

      <ConfirmDialog
        open={confirmBulk}
        onClose={() => setConfirmBulk(false)}
        title={`Resolve ${resolvableIds.length} ticket${resolvableIds.length === 1 ? '' : 's'}?`}
        description={`This marks ${resolvableIds.length === 1 ? 'it' : 'them all'} resolved and sends the school one email listing ${resolvableIds.length === 1 ? 'it' : 'every item'}.${alreadyResolvedCount ? ` ${alreadyResolvedCount} already-resolved ${alreadyResolvedCount === 1 ? 'item is' : 'items are'} skipped.` : ''}`}
        confirmLabel="Resolve and notify"
        onConfirm={() => bulkResolve(resolvableIds)}
      />

      <TicketDetailModal
        open={Boolean(selected)}
        ticket={selected}
        onClose={closeDetail}
        onSaved={onSaved}
        onDeleted={onDeleted}
      />
    </div>
  );
}
